import { useState } from 'react';
import type { SocialChannel } from './types';
import type { ClubTheme } from './themes';
import { useCreateEvent } from './queries';

interface EventComposerProps {
  clubId: string;
  channels: SocialChannel[];
  theme: ClubTheme;
  onCreated?: () => void;
  onCancel: () => void;
}

export default function EventComposer({ clubId, channels, theme, onCreated, onCancel }: EventComposerProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [location, setLocation] = useState('');
  const [maxAttendees, setMaxAttendees] = useState('');
  const [channel, setChannel] = useState('');
  const [recurrence, setRecurrence] = useState('');
  const [creditsEntry, setCreditsEntry] = useState('');
  const [creditsHostCost, setCreditsHostCost] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const createEvent = useCreateEvent(clubId);

  const canSubmit = title.trim() && startsAt && !createEvent.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    createEvent.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: endsAt ? new Date(endsAt).toISOString() : undefined,
        location: location.trim() || undefined,
        max_attendees: maxAttendees ? parseInt(maxAttendees, 10) : undefined,
        channel: channel || undefined,
        recurrence: recurrence ? (recurrence as 'weekly' | 'biweekly' | 'monthly') : undefined,
        // credits_entry and credits_host_cost are server-controlled
      },
      {
        onSuccess: () => {
          setTitle('');
          setDescription('');
          setStartsAt('');
          setEndsAt('');
          setLocation('');
          setMaxAttendees('');
          setChannel('');
          setRecurrence('');
          setCreditsEntry('');
          setCreditsHostCost('');
          onCreated?.();
        },
      },
    );
  };

  const inputStyle = {
    backgroundColor: `${theme.colors.muted}10`,
    color: theme.colors.text,
    borderColor: `${theme.colors.muted}30`,
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl p-4 space-y-3"
      style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.muted}20` }}
    >
      <h3 className="text-sm font-bold" style={{ color: theme.colors.text }}>
        New Event
      </h3>

      <input
        type="text"
        placeholder="Event title *"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
        style={{ ...inputStyle, '--tw-ring-color': theme.colors.primary } as React.CSSProperties}
      />

      <textarea
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        className="w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
        style={{ ...inputStyle, '--tw-ring-color': theme.colors.primary } as React.CSSProperties}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-[11px] font-medium" style={{ color: theme.colors.muted }}>
            Starts at *
          </label>
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
            style={{ ...inputStyle, '--tw-ring-color': theme.colors.primary } as React.CSSProperties}
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium" style={{ color: theme.colors.muted }}>
            Ends at
          </label>
          <input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
            style={{ ...inputStyle, '--tw-ring-color': theme.colors.primary } as React.CSSProperties}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="text"
          placeholder="Location (optional)"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
          style={{ ...inputStyle, '--tw-ring-color': theme.colors.primary } as React.CSSProperties}
        />
        <input
          type="number"
          placeholder="Max attendees (optional)"
          value={maxAttendees}
          onChange={(e) => setMaxAttendees(e.target.value)}
          min={1}
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
          style={{ ...inputStyle, '--tw-ring-color': theme.colors.primary } as React.CSSProperties}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {channels.length > 0 && (
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
            style={{ ...inputStyle, '--tw-ring-color': theme.colors.primary } as React.CSSProperties}
          >
            <option value="">No channel</option>
            {channels.map((ch) => (
              <option key={ch.slug} value={ch.slug}>{ch.name}</option>
            ))}
          </select>
        )}
        <select
          value={recurrence}
          onChange={(e) => setRecurrence(e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
          style={{ ...inputStyle, '--tw-ring-color': theme.colors.primary } as React.CSSProperties}
        >
          <option value="">One-time event</option>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Biweekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {/* Advanced options toggle */}
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="text-[11px] font-medium"
        style={{ color: theme.colors.primary }}
      >
        {showAdvanced ? 'Hide' : 'Show'} credit options
      </button>

      {/* Advanced options removed — credit costs are server-controlled */}

      {createEvent.isError && (
        <p className="text-xs text-red-400">
          {(createEvent.error as Error)?.message || 'Failed to create event'}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-4 py-1.5 text-xs font-medium transition-colors"
          style={{ color: theme.colors.muted }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40"
          style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
        >
          {createEvent.isPending ? 'Creating...' : 'Create Event'}
        </button>
      </div>
    </form>
  );
}
